<template>
  <!--
    AB#21836 — the Messages bell in the app bar: what this person has been
    sent lately, unread first. Clicking an item shows that message again.
    Rendered only for someone who may receive desk messages.
  -->
  <div class="mb-wrap">
    <button
      type="button"
      class="mb-btn"
      :class="{ 'mb-btn--unread': unread > 0 }"
      title="Messages"
      aria-label="Messages"
      :aria-expanded="open"
      data-test="messages-bell"
      @click="toggle"
    >
      <!-- The sidebar's Desk messages icon, so the two read as one thing. Icon only; the name is the tooltip. -->
      <Megaphone class="mb-ic" aria-hidden="true" />
      <span v-if="unread > 0" class="mb-n" data-test="messages-unread">{{
        unread > 9 ? '9+' : unread
      }}</span>
    </button>
    <Teleport to="body">
      <div v-if="open" class="mb-scrim" data-test="messages-scrim" @click="open = false"></div>
      <div v-if="open" class="mb-pop" role="dialog" aria-label="Messages" data-test="messages-pop">
        <div class="mb-head">
          Messages
          <button
            v-if="unread > 0"
            type="button"
            class="mb-link"
            data-test="messages-mark-read"
            @click="markAllRead"
          >
            Mark all read
          </button>
        </div>
        <div v-if="!items.length" class="mb-empty">No messages yet.</div>
        <button
          v-for="it in items"
          :key="it.instance_key"
          type="button"
          class="mb-it"
          :class="{ 'mb-it--unread': !it.read }"
          :data-test="`messages-item-${it.rule_id}`"
          @click="reopen(it)"
        >
          <AgentAvatar
            v-if="it.card.from.agent === 'elsa'"
            agent="elsa"
            :size="34"
            class="mb-ic2"
          />
          <UserAvatar
            v-else
            :name="it.card.from.name"
            :initials="it.card.from.initials"
            :src="it.card.from.avatar_url"
            :size="34"
            class="mb-ic2"
            :class="`mb-ic2--${it.card.tone}`"
          />
          <span class="mb-txt"
            ><b>{{ it.card.headline }}</b>
            <p>{{ it.card.text || it.card.rule.name }}</p></span
          >
          <span class="mb-ts">{{ ago(it.at) }}</span>
        </button>
      </div>
      <div v-if="reopened" class="mb-view" data-test="messages-reopened">
        <div class="mb-view-scrim" @click="reopened = null"></div>
        <div class="mb-view-center">
          <DeskMessageCard
            :card="reopened.card"
            :when="ago(reopened.at)"
            @primary="(p) => go(p)"
            @secondary="reopened = null"
            @order="(o) => go({ label: '', action: 'order', ...o })"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Megaphone } from 'lucide-vue-next';
import DeskMessageCard from './DeskMessageCard.vue';
import UserAvatar from '@/components/Shared/UserAvatar.vue';
import AgentAvatar from '@/components/Shared/AgentAvatar.vue';
import {
  deskPrimaryTarget,
  fetchInbox,
  goToDeskTarget,
  recordEvent,
  type DeskCard,
  type InboxItem,
} from '@/composables/useDeskMessages';

const props = withDefaults(defineProps<{ enabled?: boolean; pollMs?: number }>(), {
  enabled: true,
  pollMs: 300000,
});
const router = useRouter();
const route = useRoute();
const open = ref(false);
const items = ref<InboxItem[]>([]);
const unread = ref(0);
const reopened = ref<InboxItem | null>(null);
let poll: ReturnType<typeof setInterval> | null = null;

const load = async (): Promise<void> => {
  const r = await fetchInbox();
  items.value = r.items;
  unread.value = r.unread;
};
const toggle = async (): Promise<void> => {
  open.value = !open.value;
  if (open.value) await load();
};
const reopen = (it: InboxItem): void => {
  open.value = false;
  reopened.value = it;
  if (!it.read) {
    it.read = true;
    unread.value = Math.max(0, unread.value - 1);
  }
  void recordEvent(it.card, 'opened_from_bell');
};
const markAllRead = (): void => {
  for (const it of items.value) {
    if (!it.read) {
      it.read = true;
      void recordEvent(it.card, 'opened_from_bell', { reason: 'mark_all_read' });
    }
  }
  unread.value = 0;
};
const go = (p: DeskCard['primary']): void => {
  const it = reopened.value;
  reopened.value = null;
  if (it) void recordEvent(it.card, 'acted', { reason: 'from_bell', context: { action: p.label } });
  goToDeskTarget(router, route.name, deskPrimaryTarget(p));
};
const ago = (iso: string): string => {
  const d = new Date(iso);
  const day = 86400000;
  const diff = Date.now() - d.getTime();
  if (diff < day && d.getDate() === new Date().getDate())
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diff < 6 * day) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

watch(
  () => props.enabled,
  (on) => {
    if (on && poll === null) {
      void load();
      poll = setInterval(load, props.pollMs);
    }
    if (!on && poll) {
      clearInterval(poll);
      poll = null;
    }
  },
  { immediate: true }
);
onBeforeUnmount(() => poll && clearInterval(poll));
defineExpose({ load });
</script>

<style scoped>
.mb-wrap {
  position: relative;
}
.mb-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.08);
  color: #cfd4da;
  cursor: pointer;
  transition: background-color 0.12s ease;
}
.mb-btn:hover {
  background: rgba(255, 255, 255, 0.14);
}
.mb-btn--unread {
  background: rgba(79, 124, 240, 0.35);
  color: #fff;
}
.mb-ic {
  width: 18px;
  height: 18px;
}
.mb-n {
  position: absolute;
  top: -5px;
  right: -4px;
  background: #c0392b;
  color: #fff;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
}
</style>
<style>
/* Teleported to body: unscoped, token-driven. */

/*
  AB#21862 — the bell hangs off the app bar, which stays clickable while a
  drawer is open (the drawer scrim starts below it), so the popover has to
  clear the drawer layer or it opens behind the scrim. Kept below the
  teleported-popup tier (10060) so a Select inside a message still works, and
  below the Ask Atlas launcher (10070). Scale: see Shared/Drawer.vue.
*/
.mb-scrim {
  position: fixed;
  inset: 0;
  z-index: 10045;
}
.mb-pop {
  position: fixed;
  right: 160px;
  top: calc(var(--app-bar-offset, 53px) + 6px);
  width: min(94vw, 420px);
  max-height: calc(100vh - var(--app-bar-offset, 53px) - 24px);
  overflow: auto;
  background: var(--surface);
  color: var(--ink);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow-2, 0 20px 50px rgba(0, 0, 0, 0.3));
  z-index: 10046;
}
@media (max-width: 900px) {
  /* On a phone the bar has no room to anchor under the bell: centre it. */
  .mb-pop {
    right: 3vw;
    left: 3vw;
    width: auto;
  }
}
.mb-head {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
  font-weight: 600;
  display: flex;
}
.mb-link {
  margin-left: auto;
  font-weight: 400;
  font-size: 12px;
  color: var(--accent);
  background: none;
  border: 0;
  cursor: pointer;
}
.mb-empty {
  padding: 18px 16px;
  color: var(--ink-mute);
  font-size: 13px;
}
.mb-it {
  width: 100%;
  text-align: left;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid var(--border-soft);
  background: transparent;
  color: var(--ink);
  display: flex;
  gap: 12px;
  cursor: pointer;
}
.mb-it:hover {
  background: var(--surface-2);
}
.mb-it--unread {
  background: var(--surface-2);
}
.mb-it--unread .mb-txt b:before {
  content: '';
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--accent);
  margin-right: 7px;
  vertical-align: middle;
}
.mb-ic2 {
  flex: none;
}
/* An urgent message keeps its red: a ring, so it holds with a picture too. */
.mb-ic2--urgent {
  box-shadow: 0 0 0 2px var(--danger);
}
.mb-txt {
  min-width: 0;
  flex: 1;
}
.mb-txt b {
  font-size: 13.5px;
  display: block;
  overflow-wrap: anywhere;
}
.mb-txt p {
  margin: 2px 0 0;
  font-size: 12.5px;
  color: var(--ink-soft);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.mb-ts {
  font-size: 11.5px;
  color: var(--ink-mute);
  white-space: nowrap;
}
.mb-view-scrim {
  position: fixed;
  inset: 0;
  top: var(--app-bar-offset, 53px);
  background: rgba(10, 12, 16, 0.55);
  /* AB#21862 — the viewer opens from the popover, so it stays above it. */
  z-index: 10047;
}
.mb-view-center {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -48%);
  z-index: 10048;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
  border-radius: 16px;
}
</style>
